import { Link } from "react-router-dom";
import { Layers, Scale, ReceiptText, Truck, BarChart3, Gauge, ArrowUpRight } from "lucide-react";
import PageHeader from "../components/PageHeader";
import CTABanner from "../components/CTABanner";
import Crossover from "../components/Crossover";
import { Reveal } from "../components/motion";
import useMeta from "../lib/useMeta";
import { breadcrumb, serviceSchema } from "../lib/seo";

const PILLAR_FLOW = ["Source", "Compare", "Negotiate", "Order", "Deliver", "Track", "Report"];

const CAPABILITY_CARDS = [
  { icon: Layers, title: "Vendor Consolidation", text: "Reduce fragmented vendor relationships and create clearer ownership." },
  { icon: Scale, title: "Competitive Sourcing", text: "Structured quote comparison and commercial negotiation." },
  { icon: ReceiptText, title: "Rate Management", text: "Recurring categories can move toward agreed rates and buying structures." },
  { icon: Truck, title: "Order Coordination", text: "Requirement tracking from approval through dispatch, delivery and closure." },
  { icon: BarChart3, title: "Procurement MIS", text: "Monthly visibility by vendor, category, location and order status." },
  { icon: Gauge, title: "Vendor Performance", text: "Track service quality, timelines, issues and corrective actions." },
];

const WORKFLOW = [
  { num: "01", title: "Requirement", text: "Client shares item, specification, quantity, location and timeline." },
  { num: "02", title: "Source", text: "Saarthva identifies suitable vendors and sourcing options." },
  { num: "03", title: "Compare", text: "Commercials, lead time and relevant quality parameters are compared." },
  { num: "04", title: "Approve", text: "Client confirms the preferred option." },
  { num: "05", title: "Order & Deliver", text: "Vendor coordination, dispatch tracking and delivery follow-up." },
  { num: "06", title: "Close & Report", text: "Delivery acknowledgement, documentation and monthly reporting where applicable." },
];

const BENEFITS = [
  { title: "Consolidate", text: "Fragmented vendors become one accountable relationship and one consolidated view." },
  { title: "Optimise", text: "Comparative quotations and agreed rates bring commercial discipline to recurring spend." },
  { title: "Govern", text: "Structured reviews, escalation paths and vendor scorecards keep performance honest." },
];

const Bar = ({ label, pct }) => (
  <div>
    <div className="flex justify-between text-[11px] text-slate-300 mb-1.5">
      <span>{label}</span>
    </div>
    <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
      <div className="h-full rounded-full bg-saffron" style={{ width: `${pct}%` }} />
    </div>
  </div>
);

export default function ManagedProcurement() {
  useMeta({
    title: "Managed Procurement Services in India | Saarthva",
    description: "Outsource vendor sourcing, quotation comparison, negotiation, order coordination, delivery tracking and procurement reporting to Saarthva.",
    path: "/managed-procurement",
    schemas: [
      breadcrumb("Managed Procurement", "/managed-procurement"),
      serviceSchema({
        name: "Managed Procurement Services",
        description: "Outsource vendor sourcing, quotation comparison, negotiation, order coordination, delivery tracking and procurement reporting to Saarthva.",
        path: "/managed-procurement",
      }),
    ],
  });
  return (
    <div data-testid="managed-procurement-page">
      <PageHeader
        eyebrow="PROCUREMENT AS A SERVICE"
        title="A procurement department,"
        accent="without building one from scratch."
        description="Saarthva can operate as an extension of your team — managing sourcing, vendor coordination, quotations, ordering, delivery follow-ups and reporting through one accountable relationship."
      />

      <section className="bg-white border-b border-line" data-testid="mp-pillar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
          <Reveal>
            <p className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-navy max-w-3xl leading-tight">
              One accountable partner — <span className="text-saffron">from requirement to execution.</span>
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-2.5" data-testid="mp-pillar-flow">
              {PILLAR_FLOW.map((s, i) => (
                <span key={s} className="flex items-center gap-2.5">
                  <span className="rounded-full border border-line bg-mist text-navy text-[13px] font-semibold px-4 py-2">{s}</span>
                  {i < PILLAR_FLOW.length - 1 && <span className="text-saffron font-bold">→</span>}
                </span>
              ))}
            </div>
            <p className="mt-6 text-sm text-slate-600 max-w-2xl" data-testid="mp-related-links">
              Browse the <Link to="/products" className="font-semibold text-saffron hover:text-navy transition-colors">procurement categories</Link> we source across OPEX, CAPEX and MRO, or combine managed procurement with <Link to="/workplace-operations" className="font-semibold text-saffron hover:text-navy transition-colors">workplace operations support</Link> for one integrated relationship.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-white" data-testid="mp-capabilities">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <Reveal className="mb-12">
            <p className="font-mono text-[11px] tracking-[0.3em] text-steel mb-4">WHAT WE TAKE ON</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-navy">Six capabilities. One relationship.</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {CAPABILITY_CARDS.map((d, i) => {
              const Icon = d.icon;
              return (
                <Reveal key={d.title} delay={(i % 3) * 0.07}>
                  <div className="h-full bg-white rounded-2xl border border-line p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-slate-200 hover:border-steel/40" data-testid={`mp-deliverable-${i}`}>
                    <span className="w-11 h-11 rounded-xl bg-cream grid place-items-center mb-5">
                      <Icon className="w-5 h-5 text-saffron" strokeWidth={1.8} />
                    </span>
                    <h3 className="font-display text-lg font-bold text-navy">{d.title}</h3>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{d.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-mist" data-testid="mp-workflow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <Reveal className="mb-12">
            <p className="font-mono text-[11px] tracking-[0.3em] text-steel mb-4">THE BUYING JOURNEY</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-navy">From requirement to reconciliation.</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-10">
            {WORKFLOW.map((s, i) => (
              <Reveal key={s.num} delay={i * 0.06}>
                <div className="border-t-2 border-saffron/60 pt-5" data-testid={`mp-step-${s.num}`}>
                  <p className="font-mono text-saffron text-sm tracking-widest">{s.num}</p>
                  <h3 className="mt-2 font-display text-xl font-bold text-navy">{s.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="grid md:grid-cols-3 gap-5 mt-16 pt-12 border-t border-line">
            {BENEFITS.map((b, i) => (
              <Reveal key={b.title} delay={i * 0.07}>
                <div data-testid={`mp-benefit-${i}`}>
                  <h3 className="font-display text-2xl font-bold text-navy">{b.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{b.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-navy text-white grain relative overflow-hidden" data-testid="mp-mis">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <Reveal className="mb-4">
            <p className="font-mono text-[11px] tracking-[0.3em] text-saffron mb-4">MONTHLY PROCUREMENT MIS</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight leading-tight max-w-2xl">
              Visibility without another software implementation.
            </h2>
            <p className="mt-4 text-slate-300 max-w-2xl leading-relaxed">
              Saarthva can provide a structured monthly procurement view while the business is still small
              enough to stay human and flexible. Every engagement can also run through <Link to="/onedesk" className="font-semibold text-saffron hover:text-white transition-colors">Saarthva OneDesk</Link> — one tracker, one owner till closure.
            </p>
          </Reveal>
          <p className="font-mono text-[10px] tracking-[0.3em] text-slate-500 mb-8" data-testid="mis-sample-label">
            SAMPLE REPORTING VIEW — ILLUSTRATIVE FORMAT, NOT LIVE DATA
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Reveal><div className="rounded-2xl bg-white/5 border border-white/10 p-5 h-full" data-testid="mis-card-category">
              <p className="text-xs font-semibold text-slate-300 mb-4">Spend by Category</p>
              <div className="space-y-3">
                <Bar label="Housekeeping" pct={72} /><Bar label="Pantry" pct={58} /><Bar label="Stationery" pct={38} /><Bar label="Electrical & MRO" pct={44} />
              </div>
            </div></Reveal>
            <Reveal delay={0.05}><div className="rounded-2xl bg-white/5 border border-white/10 p-5 h-full" data-testid="mis-card-location">
              <p className="text-xs font-semibold text-slate-300 mb-4">Spend by Location</p>
              <div className="space-y-3">
                <Bar label="Delhi NCR" pct={80} /><Bar label="Bengaluru" pct={62} /><Bar label="Mumbai" pct={48} />
              </div>
            </div></Reveal>
            <Reveal delay={0.1}><div className="rounded-2xl bg-white/5 border border-white/10 p-5 h-full" data-testid="mis-card-vendor">
              <p className="text-xs font-semibold text-slate-300 mb-4">Vendor-wise Spend</p>
              <div className="space-y-3">
                <Bar label="Vendor A" pct={66} /><Bar label="Vendor B" pct={52} /><Bar label="Vendor C" pct={38} /><Bar label="Vendor D" pct={24} />
              </div>
            </div></Reveal>
            <Reveal delay={0.15}><div className="rounded-2xl bg-white/5 border border-white/10 p-5 h-full" data-testid="mis-card-orders">
              <p className="text-xs font-semibold text-slate-300 mb-4">Orders Open / Closed</p>
              <div className="flex items-end gap-6">
                <div><p className="font-display text-3xl font-bold text-saffron">12</p><p className="text-[11px] text-slate-400 mt-1">Open</p></div>
                <div><p className="font-display text-3xl font-bold text-white">87</p><p className="text-[11px] text-slate-400 mt-1">Closed</p></div>
              </div>
            </div></Reveal>
            <Reveal><div className="rounded-2xl bg-white/5 border border-white/10 p-5 h-full" data-testid="mis-card-delivery">
              <p className="text-xs font-semibold text-slate-300 mb-4">Delivery Status</p>
              <div className="flex h-2.5 rounded-full overflow-hidden">
                <div className="bg-emerald-400" style={{ width: "70%" }} />
                <div className="bg-saffron" style={{ width: "20%" }} />
                <div className="bg-steel" style={{ width: "10%" }} />
              </div>
              <div className="mt-3 space-y-1.5 text-[11px] text-slate-300">
                <p><span className="inline-block w-2 h-2 rounded-full bg-emerald-400 mr-2" />Delivered</p>
                <p><span className="inline-block w-2 h-2 rounded-full bg-saffron mr-2" />In transit</p>
                <p><span className="inline-block w-2 h-2 rounded-full bg-steel mr-2" />Ordered</p>
              </div>
            </div></Reveal>
            <Reveal delay={0.05}><div className="rounded-2xl bg-white/5 border border-white/10 p-5 h-full" data-testid="mis-card-savings">
              <p className="text-xs font-semibold text-slate-300 mb-4">Savings Opportunities</p>
              <ul className="space-y-2.5 text-[12px] text-slate-300">
                <li className="border-l-2 border-saffron pl-3">Consolidate pantry vendors</li>
                <li className="border-l-2 border-saffron pl-3">Move stationery to rate contract</li>
                <li className="border-l-2 border-saffron pl-3">Renegotiate housekeeping chemicals</li>
              </ul>
            </div></Reveal>
            <Reveal delay={0.1}><div className="rounded-2xl bg-white/5 border border-white/10 p-5 h-full" data-testid="mis-card-recurring">
              <p className="text-xs font-semibold text-slate-300 mb-4">Recurring Requirements</p>
              <ul className="space-y-2.5 text-[12px] text-slate-300">
                <li className="flex justify-between"><span>Pantry</span><span className="text-slate-500">Monthly</span></li>
                <li className="flex justify-between"><span>Housekeeping</span><span className="text-slate-500">Fortnightly</span></li>
                <li className="flex justify-between"><span>Stationery</span><span className="text-slate-500">Monthly</span></li>
              </ul>
            </div></Reveal>
            <Reveal delay={0.15}><div className="rounded-2xl bg-white/5 border border-white/10 p-5 h-full" data-testid="mis-card-performance">
              <p className="text-xs font-semibold text-slate-300 mb-4">Vendor Performance</p>
              <div className="space-y-3">
                <Bar label="Quality" pct={82} /><Bar label="Timelines" pct={76} /><Bar label="Responsiveness" pct={88} />
              </div>
            </div></Reveal>
          </div>
        </div>
      </section>

      <Crossover />

      <CTABanner
        title="Ready to hand over procurement?"
        accent="Talk to a Saarthi."
        copy="Start with one category or one location — and expand only when we prove value."
      />
    </div>
  );
}
